import { useGeckoNotifications } from 'features/notifications'
import { ParsedCantileverAiQueryResult, useRolodexAi } from 'features/rolodex'
import { useSageService } from 'features/sage'
import { useCallback } from 'react'
import { z } from 'zod'

const fieldTypeSchema = z.enum(['text', 'number', 'enum', 'boolean', 'date'])
type FieldType = z.infer<typeof fieldTypeSchema>

export function validFieldType(
  fieldType: string | undefined
): FieldType | undefined {
  const { success, data } = fieldTypeSchema.safeParse(fieldType)
  return success ? data : undefined
}

// value can realistically be string/number/boolean/null depending on field_type
const extractedValueSchema = z.object({
  value: z.union([z.string(), z.number(), z.boolean(), z.null()]),
  confidence: z.number().min(0).max(1),
  source_snippets: z.array(z.number().int().nonnegative()),
  explanation: z.string().min(1),
})

const extractionResultSchema = z.object({
  field_name: z.string(),
  field_type: fieldTypeSchema,
  values: z.array(extractedValueSchema).readonly(),
  reasoning: z.string().optional(),
})

export type ExtractionResult = z.infer<typeof extractionResultSchema>

interface Chunk {
  text: string
  section?: string
  pageNumber?: number
}

type ChunkMetadata =
  ParsedCantileverAiQueryResult['results'][number]['metadata']
function renderChunk(result: ChunkMetadata): Chunk | undefined {
  if (!result.text_content) return undefined
  return {
    text: result.text_content,
    section: result.section ?? undefined,
    pageNumber: result.page_number ?? undefined,
  }
}

function createPrompt(
  fieldName: string,
  fieldType: FieldType,
  chunks: Chunk[]
): string {
  const chunksBlock = chunks
    .map((chunk, index) => {
      const headerParts: string[] = []
      if (chunk.section) headerParts.push(`section: ${chunk.section}`)
      if (chunk.pageNumber !== null && chunk.pageNumber !== undefined)
        headerParts.push(`page ${chunk.pageNumber}`)

      const header =
        headerParts.length > 0
          ? `(${headerParts.join(', ')})`
          : '(no extra metadata)'

      return `[${index}] ${header}\n"${chunk.text}"`
    })
    .join('\n\n')

  return (
    `Your task is to extract the most plausible values for the field:\n\n` +
    `  Field Name: ${fieldName}\n` +
    `  Field Type: ${fieldType}\n\n` +
    `You are given a list of text snippets that may contain explicit, implicit, partial, or approximate values for this field.\n\n` +
    `General rules:\n` +
    `- Consider all snippets; do not assume the value appears only once.\n` +
    `- You may return multiple plausible candidates if appropriate.\n` +
    `- For each candidate value, provide:\n` +
    `  - A normalized value appropriate to the field type.\n` +
    `  - A confidence score between 0 and 1.\n` +
    `  - A short explanation referencing the relevant snippet(s).\n` +
    `  - The index numbers of the snippet(s) used.\n\n` +
    `Field-type specific rules:\n` +
    `- date → Normalize to ISO 8601 (YYYY-MM-DD) when possible. If incomplete, return your best estimate with lower confidence.\n` +
    `- number → Extract the numeric value. Normalize units only if they are explicit in the text.\n` +
    `- enum → Return exactly the string(s) appearing in the source, unless normalization is obvious from the context.\n` +
    `- boolean → Interpret affirmations/negations, but be conservative with confidence.\n` +
    `- text → Extract the shortest precise text span that best fits the field name.\n` +
    `- If the field does not appear, return an empty "values" list and explain why.\n\n` +
    `You must respond ONLY in this JSON format:\n\n` +
    `{\n` +
    `  "field_name": "${fieldName}",\n` +
    `  "field_type": "${fieldType}",\n` +
    `  "values": [\n` +
    `    {\n` +
    `      "value": "<candidate value>",\n` +
    `      "confidence": <float 0-1>,\n` +
    `      "source_snippets": [<indices>],\n` +
    `      "explanation": "<short explanation>"\n` +
    `    }\n` +
    `  ],\n` +
    `  "reasoning": "<optional brief summary of how you evaluated candidates>"\n` +
    `}\n\n` +
    `Here are the text snippets, each with an index:\n\n` +
    `${chunksBlock}\n\n` +
    `Extract the most plausible values for "${fieldName}" and respond strictly in the JSON format above.`
  )
}

export interface ExtractFieldValuesParams {
  documentRolodexId: string
  fieldName: string
  fieldType: FieldType
}

interface SageInferenceApi {
  extractFieldValues: (
    params: ExtractFieldValuesParams
  ) => Promise<ExtractionResult>
}

const CHUNKS_TOP_K = 20

function stripMarkdownCodeFences(text: string): string {
  // Remove ```json and ``` wrapping
  return text.replace(/^```(?:json)?\s*\n?/, '').replace(/\n?```\s*$/, '')
}

export function parseLlmExtractionResult(
  response: string | unknown,
  fieldName: string,
  fieldType: FieldType
): ExtractionResult {
  // Strip markdown code fences if present
  const resultText =
    typeof response === 'string' ? stripMarkdownCodeFences(response) : response

  try {
    const parsedResult = JSON.parse(resultText as string)
    return extractionResultSchema.parse(parsedResult)
  } catch (error) {
    console.error('Error parsing extraction result:', error)
    return {
      field_name: fieldName,
      field_type: fieldType,
      values: [],
    }
  }
}

export function useSageInferenceApi(): SageInferenceApi {
  const fetchChunks = useRolodexAi()
  const { fetchVertexQuery } = useSageService()
  const { errorNotification } = useGeckoNotifications()

  const extractFieldValues = useCallback(
    async ({
      documentRolodexId,
      fieldName,
      fieldType,
    }: ExtractFieldValuesParams) => {
      const chunksResult = await fetchChunks({
        query: fieldName,
        load_options: {
          ids: [documentRolodexId],
        },
        top_k: CHUNKS_TOP_K,
        re_rank: false,
        include_embeddings: false,
      })
      const chunks = chunksResult.results
        .map((r) => renderChunk(r.metadata))
        .filter((chunk): chunk is Chunk => !!chunk)
      const prompt = createPrompt(fieldName, fieldType, chunks)

      const response = await fetchVertexQuery({
        query: prompt,
      })

      try {
        return parseLlmExtractionResult(response.result, fieldName, fieldType)
      } catch (error) {
        console.error('Error parsing extraction result:', error)
        errorNotification(
          'Error parsing extraction result. Please make a note of the field and contact support.'
        )
        return {
          field_name: fieldName,
          field_type: fieldType,
          values: [],
        }
      }
    },
    [fetchChunks, fetchVertexQuery, errorNotification]
  )

  return { extractFieldValues }
}
